/**
 * What one recorded change did to a row's columns.
 *
 * Shared by the panel beside a row and the database-wide activity history,
 * because a change reads the same in both: the removed values struck through,
 * the added ones beside them, and a password or a redacted column described
 * rather than diffed. What differs between the two lists is everything *around*
 * a change -- which row, which table, where the write came in through -- and
 * none of that is here.
 */
import { useTranslation } from 'react-i18next'

import type { HistoryChange } from './history'

export function HistoryChanges({ changes }: { changes: readonly HistoryChange[] }) {
  const { t } = useTranslation()

  if (changes.length === 0) return null

  return (
    <dl className="mt-2 space-y-2">
      {changes.map((change) => (
        <div key={change.key} className="rounded border bg-background px-2 py-1.5">
          <dt
            className="truncate text-xs font-medium text-muted-foreground"
            title={change.name}
          >
            {change.name}
          </dt>
          <dd className="mt-0.5 text-sm break-words">
            {change.kind === 'password' ? (
              <span className="text-muted-foreground">
                {t(`history.password.${change.change}`)}
              </span>
            ) : change.kind === 'redacted' ? (
              <span className="text-muted-foreground">{t('history.redacted')}</span>
            ) : (
              <>
                {change.removed.map((item) => (
                  <span
                    key={`-${item.key}`}
                    className="mr-2 inline-block text-destructive line-through"
                  >
                    {item.text}
                  </span>
                ))}
                {change.added.map((item) => (
                  <span key={`+${item.key}`} className="mr-2 inline-block text-success">
                    {item.text}
                  </span>
                ))}
              </>
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}
